import React from 'react';
import { AddElementSlot } from '../addElementSlot/AddElementSlot';
import { parseDefinitions } from '../runtime/definitionParsing';
import { appendStructurePath, formatStructurePath } from './structurePath';
import type {
    ElementDefinition,
    ElementInsertionSlotKind,
    RenderContext,
    RenderContextKind,
    RenderRuntime,
} from './types';
import type { StructurePath } from './structurePath';

export { parseDefinitions };

export function combineRenderableContent(
    contentResults: readonly Promise<boolean>[]
): Promise<boolean> {
    // An empty definition list is intentional, visible authoring content. Containers
    // with multiple child lists omit empty branches before aggregating so an empty
    // slot does not mask hidden content in another branch.
    if (contentResults.length === 0) {
        return Promise.resolve(true);
    }

    return Promise.all(contentResults).then((results) => results.some(Boolean));
}

export function renderDefinitionList(
    definitions: ElementDefinition[],
    ctx: RenderContext,
    runtime: RenderRuntime,
    listPath: StructurePath,
    slotKind: ElementInsertionSlotKind
): {
    nodes: React.ReactNode[];
    ready: Promise<void>;
    hasRenderableContent: Promise<boolean>;
} {
    const renderedChildren = definitions.map((definition, index) => {
        const element = runtime.createElementFromDefinition(definition);
        return {
            definition,
            index,
            rendered: runtime.withElementLocation(
                {
                    elementPath: appendStructurePath(listPath, index),
                    listPath,
                    contextKind: ctx.kind,
                    // Keep explicit slot semantics in location metadata. Some placements
                    // (for example root-level repeat children) are template-root slots
                    // even though their list path is nested under settings.
                    slotKind,
                    index,
                    siblingCount: definitions.length,
                },
                () => element.toReactNode(ctx, runtime)
            ),
        };
    });

    const nodes = renderedChildren.map(({ definition, index, rendered }) => (
        <React.Fragment key={`${definition.type}-${index}`}>{rendered.node}</React.Fragment>
    ));

    const contentResults = renderedChildren.map((item) => item.rendered.hasRenderableContent);
    const hasRenderableContent = combineRenderableContent(contentResults);
    const ready = Promise.all([
        ...renderedChildren.map((item) => item.rendered.ready),
        hasRenderableContent,
    ]).then(() => undefined);

    return {
        nodes,
        ready,
        hasRenderableContent,
    };
}

export function renderDefinitionListWithAddElementSlot(
    definitions: ElementDefinition[],
    ctx: RenderContext,
    runtime: RenderRuntime,
    listPath: StructurePath,
    contextKind: RenderContextKind,
    slotKind: ElementInsertionSlotKind,
    buttonLabel = 'Add element'
): {
    nodes: React.ReactNode[];
    ready: Promise<void>;
    hasRenderableContent: Promise<boolean>;
} {
    const rendered = renderDefinitionList(definitions, ctx, runtime, listPath, slotKind);

    if (!runtime.showEditor) {
        return rendered;
    }

    return {
        nodes: [
            ...rendered.nodes,
            <AddElementSlot
                key={`add-element-slot-${formatStructurePath(listPath)}`}
                runtime={runtime}
                listPath={listPath}
                contextKind={contextKind}
                slotKind={slotKind}
                buttonLabel={buttonLabel}
                renderContext={ctx}
            />,
        ],
        ready: rendered.ready,
        hasRenderableContent: rendered.hasRenderableContent,
    };
}
